"use client";

import clsx from "clsx";
import { Link } from "@repo/next-utils/router";
import { Text } from "@repo/ui/text";
import {
  TYPOGRAPHY_BODY3_BOLD,
  TYPOGRAPHY_LABEL3_BLACK,
  TYPOGRAPHY_SMALLPRINT,
} from "@/theme/topshop/typography/typography-constants";
import type { BannerContent } from "./global-banner.types";
import styles from "./global-banner.module.css";

type GlobalBannerProps = {
  content: BannerContent;
};

export const GlobalBanner = ({ content }: GlobalBannerProps) => {
  const renderSubtitle = () => {
    if (!content.subtitle) return null;

    // Cyber Monday periods: highlight "NEW CUSTOMERS" in green
    if (content.colorScheme === "cyberMonday") {
      const parts = content.subtitle.split("NEW CUSTOMERS");
      if (parts.length === 2) {
        return (
          <Text className={TYPOGRAPHY_BODY3_BOLD} suppressHydrationWarning>
            <span className={styles.subtitleHighlight}>NEW CUSTOMERS</span>
            {parts[1]}
          </Text>
        );
      }
    }

    // Prevent "(STANDARD SERVICE)" from breaking across lines
    const subtitleWithNonBreakingSpace = content.subtitle.replace(
      /\(STANDARD SERVICE\)/g,
      "(STANDARD\u00A0SERVICE)",
    );

    return (
      <Text
        className={clsx(
          TYPOGRAPHY_BODY3_BOLD,
          content.colorScheme === "black" && styles.subtitleHighlight,
        )}
        suppressHydrationWarning
      >
        {subtitleWithNonBreakingSpace}
      </Text>
    );
  };

  const isSingleLine =
    [content.title, content.subtitle, content.text].filter(Boolean).length <= 1;

  const bannerContent = (
    <div
      className={clsx(styles.content, isSingleLine && styles.contentCentered)}
      suppressHydrationWarning
    >
      {content.title && (
        <Text
          className={clsx(
            TYPOGRAPHY_BODY3_BOLD,
            styles.title,
            content.colorScheme === "lastChanceBlackFriday" &&
              styles.titleHighlight,
            content.colorScheme === "cyberMonday" && styles.titleHighlight,
          )}
          suppressHydrationWarning
        >
          {content.title}
        </Text>
      )}
      {renderSubtitle()}

      <Text
        className={clsx(
          TYPOGRAPHY_SMALLPRINT,
          styles.text,
          content.colorScheme === "cyberMonday" && styles.textHighlight,
        )}
        suppressHydrationWarning
      >
        {content.text && content.text}{" "}
        {content.code && (
          <strong
            className={clsx(styles.codeHighlight, TYPOGRAPHY_LABEL3_BLACK)}
            suppressHydrationWarning
          >
            {content.code}
          </strong>
        )}
      </Text>
    </div>
  );

  const bannerClassName = clsx(
    styles.banner,
    styles[content.colorScheme],
    content.clickable && styles.clickable,
  );

  if (content.clickable && content.href) {
    const ariaLabel =
      [content.title, content.subtitle].filter(Boolean).join(" - ") +
      " - Click to view sale items";

    return (
      <Link
        href={content.href}
        className={bannerClassName}
        aria-label={ariaLabel}
        suppressHydrationWarning
        data-testid="global-banner"
      >
        {bannerContent}
      </Link>
    );
  }

  return (
    <div
      className={bannerClassName}
      suppressHydrationWarning
      data-testid="global-banner"
    >
      {bannerContent}
    </div>
  );
};
